import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useAuthStore from "../store/authStore";
import { userAPI } from "../services/api";
import { showToast } from "../components/Toast";

const Settings = () => {
  const { user, fetchUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile state - populated from actual user
  const [profileData, setProfileData] = useState({
    full_name: "",
    email: "",
    company: "",
    role: "",
    avatar_url: "",
  });

  // Account/password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNotifications: {
      workflowAlerts: true,
      weeklyReports: true,
      productUpdates: false,
      billing: true,
    },
    inAppNotifications: {
      workflowStatus: true,
      taskCompletion: true,
      errors: true,
    },
  });

  // Billing data (mock for now)
  const [billingData] = useState({
    plan: "Pro",
    billingCycle: "Monthly",
    amount: "$49/month",
    nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    paymentMethod: "•••• 4242",
  });

  const tabs = [
    { id: "profile", name: "Profile", icon: "👤", description: "Personal information" },
    { id: "account", name: "Account & Security", icon: "🔐", description: "Password and 2FA" },
    { id: "billing", name: "Billing", icon: "💳", description: "Plan and payment" },
    { id: "notifications", name: "Notifications", icon: "🔔", description: "Email and alerts" },
    { id: "api", name: "API Keys", icon: "🔑", description: "Manage API access" },
  ];

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || "",
        email: user.email || "",
        company: user.company || "",
        role: user.role || "",
        avatar_url: user.avatar_url || "",
      });
    }
  }, [user]);

  const handleProfileSave = async () => {
    if (!user || !user.id) {
      showToast.error("User not found. Please log in again.");
      return;
    }

    setSaving(true);
    try {
      await userAPI.updateProfile(user.id, profileData);
      await fetchUser(); // Refresh user data in store
      showToast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Failed to update profile:", error);
      showToast.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      showToast.error("Please fill in all password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast.error("New passwords don't match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showToast.error("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    try {
      await userAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      showToast.success("Password changed successfully!");
    } catch (error) {
      console.error("Failed to change password:", error);
      showToast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle2FA = async () => {
    setSaving(true);
    try {
      if (twoFactorEnabled) {
        // Disable 2FA
        await userAPI.disable2FA("000000"); // TODO: Show modal to enter code
        setTwoFactorEnabled(false);
        showToast.success("Two-factor authentication disabled");
      } else {
        // Enable 2FA
        const response = await userAPI.enable2FA();
        setTwoFactorEnabled(true);
        showToast.success("Two-factor authentication enabled. Scan the QR code in your authenticator app.");
        // TODO: Show QR code modal with response.qr_code_url
      }
    } catch (error) {
      console.error("Failed to toggle 2FA:", error);
      showToast.error("Failed to update 2FA settings");
    } finally {
      setSaving(false);
    }
  };

  const ProfileTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Settings</h2>
        <p className="text-gray-600">Update your personal information and avatar</p>
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
          {profileData.full_name?.charAt(0) || user?.email?.charAt(0) || "U"}
        </div>
        <div>
          <button className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
            Change Avatar
          </button>
          <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF. Max 2MB.</p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
          <input
            type="text"
            value={profileData.full_name}
            onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="john@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
          <input
            type="text"
            value={profileData.company}
            onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Acme Corp"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
          <input
            type="text"
            value={profileData.role}
            onChange={(e) => setProfileData({ ...profileData, role: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Product Manager"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-6 border-t border-gray-200 flex justify-end">
        <button
          onClick={handleProfileSave}
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </motion.div>
  );

  const AccountTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Account & Security</h2>
        <p className="text-gray-600">Manage your password and security settings</p>
      </div>

      {/* Change Password Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
          <input
            type="password"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter current password"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Min. 8 characters"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Re-enter new password"
            />
          </div>
        </div>

        <button
          onClick={handlePasswordChange}
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {saving ? "Changing..." : "Change Password"}
        </button>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
            <p className="text-sm text-gray-600 mt-1">Add an extra layer of security to your account</p>
          </div>
          <button
            onClick={handleToggle2FA}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              twoFactorEnabled ? "bg-blue-600" : "bg-gray-300"
            } disabled:opacity-50`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                twoFactorEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {twoFactorEnabled && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">✓ Two-factor authentication is enabled</p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const BillingTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Billing & Subscription</h2>
        <p className="text-gray-600">Manage your plan and payment methods</p>
      </div>

      {/* Current Plan */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{billingData.plan} Plan</h3>
            <p className="text-sm text-gray-600">{billingData.amount} • {billingData.billingCycle}</p>
          </div>
          <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">Active</span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Next billing date</p>
            <p className="font-medium text-gray-900">{billingData.nextBillingDate}</p>
          </div>
          <div>
            <p className="text-gray-600">Payment method</p>
            <p className="font-medium text-gray-900">{billingData.paymentMethod}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-blue-200 flex gap-3">
          <button className="px-4 py-2 text-sm bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-300">
            Change Plan
          </button>
          <button className="px-4 py-2 text-sm bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-300">
            Update Payment
          </button>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing History</h3>
        <div className="space-y-3">
          {[
            { date: "Mar 1, 2024", amount: "$49.00", status: "Paid" },
            { date: "Feb 1, 2024", amount: "$49.00", status: "Paid" },
            { date: "Jan 1, 2024", amount: "$49.00", status: "Paid" },
          ].map((invoice, index) => (
            <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div>
                <p className="font-medium text-gray-900">{invoice.date}</p>
                <p className="text-sm text-gray-600">{invoice.amount}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">{invoice.status}</span>
                <button className="text-blue-600 hover:underline text-sm font-medium">Download</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  const NotificationsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Notification Preferences</h2>
        <p className="text-gray-600">Manage how you receive notifications</p>
      </div>

      {/* Email Notifications */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {Object.entries(notificationPrefs.emailNotifications).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-gray-900 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                <p className="text-sm text-gray-600">Receive notifications about {key.toLowerCase()}</p>
              </div>
              <button
                onClick={() => setNotificationPrefs({
                  ...notificationPrefs,
                  emailNotifications: {
                    ...notificationPrefs.emailNotifications,
                    [key]: !value,
                  },
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  value ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* In-App Notifications */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">In-App Notifications</h3>
        <div className="space-y-4">
          {Object.entries(notificationPrefs.inAppNotifications).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-gray-900 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                <p className="text-sm text-gray-600">Show in-app notifications for {key.toLowerCase()}</p>
              </div>
              <button
                onClick={() => setNotificationPrefs({
                  ...notificationPrefs,
                  inAppNotifications: {
                    ...notificationPrefs.inAppNotifications,
                    [key]: !value,
                  },
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  value ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200 flex justify-end">
        <button
          onClick={() => showToast.success("Notification preferences saved!")}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Save Preferences
        </button>
      </div>
    </motion.div>
  );

  const APITab = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">API Keys</h2>
          <p className="text-gray-600">Manage API keys for programmatic access</p>
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
          + Generate New Key
        </button>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ Security Notice:</strong> API keys provide full access to your account. Keep them secret and never share them publicly.
        </p>
      </div>

      <div className="space-y-3">
        {[
          { id: 1, name: "Production API Key", key: "ak_live_••••••••••••1234", created: "Jan 15, 2024", lastUsed: "2 hours ago" },
          { id: 2, name: "Development API Key", key: "ak_test_••••••••••••5678", created: "Feb 10, 2024", lastUsed: "5 days ago" },
        ].map((apiKey) => (
          <div key={apiKey.id} className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{apiKey.name}</h3>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-700">{apiKey.key}</code>
                <div className="flex gap-4 mt-3 text-sm text-gray-600">
                  <span>Created: {apiKey.created}</span>
                  <span>•</span>
                  <span>Last used: {apiKey.lastUsed}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="text-blue-600 hover:underline text-sm font-medium">Regenerate</button>
                <button className="text-red-600 hover:underline text-sm font-medium">Revoke</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account preferences and settings</p>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white border border-gray-200 rounded-xl p-2 mb-6 flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? "bg-blue-50 text-blue-700 font-medium"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <span className="text-lg">{tab.icon}</span>
              <div className="text-left hidden sm:block">
                <div className="text-sm">{tab.name}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8">
          <AnimatePresence mode="wait">
            {activeTab === "profile" && <ProfileTab key="profile" />}
            {activeTab === "account" && <AccountTab key="account" />}
            {activeTab === "billing" && <BillingTab key="billing" />}
            {activeTab === "notifications" && <NotificationsTab key="notifications" />}
            {activeTab === "api" && <APITab key="api" />}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Settings;
